
import React, { useEffect, useState } from "react";
import "./CategoryManager.css";

const API_URL = "/api/categories.php";
const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

export default function CategoryManager(props) {
    const [categories, setCategories] = useState([]);
    const [name, setName] = useState("");
    const [color, setColor] = useState("#6366F1");
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        async function loadCategories() {
            try {
                const response = await fetch(API_URL);
                if (!response.ok) throw new Error("Laden fehlgeschlagen");

                const data = await response.json();
                if (!Array.isArray(data)) {
                    throw new Error("Ungültige Serverantwort");
                }
                setCategories(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        loadCategories();
    }, []);

    async function persist(nextCategories) {
        setSaving(true);
        setError("");

        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(nextCategories)
            });

            if (!response.ok) {
                throw new Error("Speichern fehlgeschlagen");
            }

            setCategories(nextCategories);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setSaving(false);
        }
    }

    function resetForm() {
        setName("");
        setColor("#6366F1");
        setEditingId(null);
    }

    async function handleSubmit(event) {
        event.preventDefault();

        const trimmedName = name.trim();

        if (!trimmedName || !HEX_REGEX.test(color)) {
            setError("Ungültiger Name oder HEX-Farbwert.");
            return;
        }

        if (categories.some(category =>
            category.id !== editingId &&
            category.name.toLowerCase() === trimmedName.toLowerCase()
        )) {
            setError("Diese Kategorie existiert bereits.");
            return;
        }

        let nextCategories;

        if (editingId !== null) {
            nextCategories = categories.map(category =>
                category.id === editingId
                    ? { ...category, name: trimmedName, color: color.toUpperCase() }
                    : category
            );
        } else {
            const newCategory = {
                id: crypto.randomUUID(),
                name: trimmedName,
                color: color.toUpperCase()
            };

            nextCategories = [...categories, newCategory];
        }

        if (await persist(nextCategories)) {
            resetForm();
        }
    }

    function handleEdit(category) {
        setEditingId(category.id);
        setName(category.name);
        setColor(category.color);
        setError("");
    }

    async function handleDelete(id) {
        if (!window.confirm("Kategorie wirklich löschen?")) return;

        const nextCategories = categories.filter(
            category => category.id !== id
        );

        if (await persist(nextCategories) && editingId === id) {
            resetForm();
        }
    }

    return (
        <div className="category-manager" onClick={props.onClose}>
            <form onSubmit={handleSubmit} className="category-form">
                <label>
                    Name
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Kategoriename"
                        maxLength={60}
                        required
                    />
                </label>

                <label>
                    Farbe
                    <div className="color-input">
                        <input
                            type="color"
                            value={HEX_REGEX.test(color) ? color : "#6366F1"}
                            onChange={e => setColor(e.target.value)}
                        />
                        <input
                            type="text"
                            value={color}
                            onChange={e => setColor(e.target.value)}
                            placeholder="#6366F1"
                            maxLength={7}
                            required
                        />
                    </div>
                </label>

                <div className="category-actions">
                    <button type="submit" disabled={saving || loading}>
                        {editingId ? "Speichern" : "Hinzufügen"}
                    </button>

                    {editingId && (
                        <button type="button" onClick={resetForm}>
                            Abbrechen
                        </button>
                    )}
                </div>
            </form>

            {error && <p role="alert" className="category-error">{error}</p>}

            {loading ? (
                <p>Kategorien werden geladen...</p>
            ) : (
                <div className="category-list">
                    {categories.map(category => (
                        <div className="category-item" key={category.id}>
                            <span
                                className="category-color"
                                style={{ backgroundColor: category.color }}
                            />

                            <div className="category-info">
                                <strong>{category.name}</strong>
                                <small>{category.color}</small>
                            </div>

                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleEdit(category)}
                            >
                                Bearbeiten
                            </button>

                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleDelete(category.id)}
                            >
                                Löschen
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
